// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import translate from "@insite/client-framework/Translate";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import CheckboxGroup, { CheckboxGroupComponentProps, CheckboxGroupProps } from "@insite/mobius/CheckboxGroup";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import { CustomAppState } from "@tps/App/Store/Reducers";
import AddressInfoDisplay from "@tps/Overrides/Components/AddressInfoDisplay";
import React from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    shipTo: ShipToModel;
    onEdit: () => void;
    extendedStyles?: ShippingAddressInfoDisplayStyles;
    // XNG-Change
    hasMultipleWarehouses?: boolean;
    hasWillCall?: boolean;
}

export interface ShippingAddressInfoDisplayStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    editLink?: LinkPresentationProps;
    addressGridItem?: GridItemProps;
    address?: AddressInfoDisplayStyles;
    // XNG-Change
    oneTimeAddressHeading?: TypographyPresentationProps;
    saveOneTimeAddressCheckboxGroup?: CheckboxGroupProps;
}

export const shippingAddressInfoDisplayStyles: ShippingAddressInfoDisplayStyles = {
    container: { gap: 0 },
    headingGridItem: { width: 12 },
    heading: {
        weight: 600,
        css: css`
            margin: 0 1rem 0 0;
        `,
    },
    addressGridItem: { width: 12 },
};

const ShippingAddressInfoDisplay = ({
    shipTo,
    onEdit,
    extendedStyles,
    hasMultipleWarehouses,
    hasWillCall,
}: OwnProps) => {
    const styles = mergeToNew(shippingAddressInfoDisplayStyles, extendedStyles);
    // XNG-Change: TPS-934 add save address checkbox
    const saveOneTimeAddress = useAppSelector(state => (state as CustomAppState).xCheckout.saveOneTimeAddress);
    const dispatch = useDispatch();

    const saveOneTimeAddressChangeHandler: CheckboxProps["onChange"] = (_, checked) => {
        dispatch({ type: "xCheckout/SetSaveOneTimeAddress", saveOneTimeAddress: checked });
    };

    return (
        <GridContainer {...styles.container} data-test-selector="shippingAddressInfoDisplay_container">
            <GridItem {...styles.headingGridItem}>
                <Typography {...styles.heading}>{translate("Shipping Address")}</Typography>
                {!hasMultipleWarehouses && !hasWillCall && (
                    <Link
                        {...styles.editLink}
                        onClick={onEdit}
                        data-test-selector="checkoutReviewAndSubmit_changeShipping"
                    >
                        {/* XNG-Change change label name */}
                        {translate("Edit Option")}
                        <VisuallyHidden>{translate("Shipping Address")}</VisuallyHidden>
                    </Link>
                )}
            </GridItem>
            {/* XNG-Change */}
            {shipTo.oneTimeAddress && (
                <GridItem width={12}>
                    <Typography {...styles.oneTimeAddressHeading}>{translate("One-Time Address")}</Typography>
                </GridItem>
            )}
            <GridItem {...styles.addressGridItem}>
                <AddressInfoDisplay
                    {...shipTo}
                    state={shipTo.state ? shipTo.state.abbreviation : undefined}
                    country={shipTo.country ? shipTo.country.abbreviation : undefined}
                    extendedStyles={styles.address}
                    {...{ customerNum: shipTo.customerNumber }}
                    // XNG-Change
                    hasMultipleWarehouses={hasMultipleWarehouses}
                    hasWillCall={hasWillCall}
                />
            </GridItem>
            {/* Xng-Changes: TPS-934 add save address checkbox */}
            {shipTo.oneTimeAddress && (
                <GridItem width={12}>
                    <CheckboxGroup {...styles.saveOneTimeAddressCheckboxGroup}>
                        <Checkbox
                            onChange={saveOneTimeAddressChangeHandler}
                            checked={saveOneTimeAddress}
                            data-test-selector="shippingAddressInfoDisplay_saveOneTimeAddressCheckbox"
                        >
                            {translate("Save Address?")}
                        </Checkbox>
                    </CheckboxGroup>
                </GridItem>
            )}
        </GridContainer>
    );
};

export default ShippingAddressInfoDisplay;
